import { useState, useEffect, useCallback } from 'react'
import { apiService } from '../services/api'

export interface DepreciationRule {
 id: string
 label: string
 type: 'mileage' | 'age' | 'condition'
 threshold?: number | null
 conditionValue?: string | null
 percentage: number
 isActive: boolean
 createdAt?: string
 updatedAt?: string
}

export interface DepreciationCalculation {
 appliedRules: DepreciationRule[]
 totalPercentage: number
 suggestedValue: number
}

export function useDepreciationRules() {
 const [rules, setRules] = useState<DepreciationRule[]>([])
 const [isLoading, setIsLoading] = useState(false)
 const [error, setError] = useState<string | null>(null)

 const fetchRules = useCallback(async () => {
 setIsLoading(true)
 setError(null)
 try {
 const data = await apiService.getDepreciationRules()
 setRules(data as DepreciationRule[])
 } catch (err: any) {
 setError(err?.message || 'Erro ao carregar regras')
 } finally {
 setIsLoading(false)
 }
 }, [])

 useEffect(() => {
 fetchRules()
 }, [fetchRules])

 const addRule = async (rule: Omit<DepreciationRule, 'id' | 'createdAt' | 'updatedAt'>) => {
 const created = await apiService.createDepreciationRule(rule as any)
 setRules(prev => [...prev, created as DepreciationRule])
 return created
 }

 const updateRule = async (id: string, updates: Partial<DepreciationRule>) => {
 const updated = await apiService.updateDepreciationRule(id, updates as any)
 setRules(prev => prev.map(r => r.id === id ? { ...r, ...(updated as DepreciationRule) } : r))
 return updated
 }

 const deleteRule = async (id: string) => {
 await apiService.deleteDepreciationRule(id)
 setRules(prev => prev.filter(r => r.id !== id))
 }

 const calculate = (
 mileage: number,
 modelYear: number,
 condition: string,
 baseValue: number
 ): DepreciationCalculation => {
 const age = new Date().getFullYear() - modelYear
 const active = rules.filter(r => r.isActive)

 // Para mileage e age: aplica apenas a regra com maior threshold que se encaixa
 const mileageRule = active
 .filter(r => r.type === 'mileage' && r.threshold != null && mileage >= r.threshold)
 .sort((a, b) => (b.threshold ?? 0) - (a.threshold ?? 0))[0]

 const ageRule = active
 .filter(r => r.type === 'age' && r.threshold != null && age >= r.threshold)
 .sort((a, b) => (b.threshold ?? 0) - (a.threshold ?? 0))[0]

 const conditionRule = active.find(
 r => r.type === 'condition' && r.conditionValue === condition
 )

 const appliedRules = [mileageRule, ageRule, conditionRule].filter((r): r is DepreciationRule => !!r)
 const totalPercentage = appliedRules.reduce((sum, r) => sum + Number(r.percentage), 0)
 const suggestedValue = Math.round(baseValue * (1 + totalPercentage / 100))

 return { appliedRules, totalPercentage, suggestedValue }
 }

 return { rules, isLoading, error, addRule, updateRule, deleteRule, calculate, refetch: fetchRules }
}
